import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, ArrowRight, CheckCircle, WarningCircle, CurrencyDollar,
  Lock, Users, House, Briefcase, Gavel, HandCoins, TreeStructure,
  Heart, Bank, WarningDiamond, Scales
} from "@phosphor-icons/react";

/* ============================================================
   WHAT IS A TRUST TAB
   ============================================================ */
function WhatIsATrustTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="what-is-trust-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-indigo-900/20 flex items-center justify-center">
            <ShieldCheck size={20} weight="bold" className="text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">What is a Trust?</h2>
            <p className="text-xs text-zinc-400">A legal arrangement where one party holds assets on behalf of another</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          A trust is a legal entity that holds and manages assets for the benefit of specific people or purposes. Think of it as a lockbox with rules: you put your assets inside, appoint someone to manage it (the trustee), and write instructions for who gets what and when (the beneficiaries). Once assets are in the trust, they're no longer legally "yours" — they belong to the trust. This separation is what gives trusts their power: asset protection, tax benefits, privacy, and control from beyond the grave.
        </p>
      </div>

      {/* Three parties */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-4 font-medium">The Three Parties in Every Trust</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users size={14} weight="bold" className="text-indigo-400" />
              <span className="text-xs font-medium text-zinc-200">Grantor (Settlor)</span>
            </div>
            <p className="text-lg font-data font-bold text-indigo-400 mb-1">You</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">The person who creates the trust and transfers assets into it. You decide the rules, who benefits, and under what conditions. Once the trust is irrevocable, you give up control.</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase size={14} weight="bold" className="text-emerald-400" />
              <span className="text-xs font-medium text-zinc-200">Trustee</span>
            </div>
            <p className="text-lg font-data font-bold text-emerald-400 mb-1">Manager</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">The person or entity that manages the trust assets according to the rules you set. Can be you (revocable trust), a family member, an attorney, or a professional trust company.</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Heart size={14} weight="bold" className="text-rose-400" />
              <span className="text-xs font-medium text-zinc-200">Beneficiary</span>
            </div>
            <p className="text-lg font-data font-bold text-rose-400 mb-1">Recipient</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">The person(s) who receive the benefits from the trust — income, property, or assets — according to the terms you wrote. Can be your children, spouse, charity, or even yourself.</p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <TreeStructure size={14} weight="bold" className="text-indigo-400" /> How a Trust Works — Step by Step
          </h3>
        </div>
        <div className="p-5 space-y-3">
          {[
            "You create a trust document with an attorney. This document spells out every rule: who gets what, when they get it, who manages it, and what happens if circumstances change.",
            "You transfer assets into the trust — real estate (via new deed), bank accounts, brokerage accounts, life insurance policies, business interests, crypto wallets, and anything else you want protected.",
            "Once assets are in the trust, legal ownership changes. Your name is no longer on the title — the trust's name is. This is what creates the legal separation that enables protection.",
            "The trustee manages the assets according to your instructions. They invest, distribute income, pay taxes, and make decisions — but only within the boundaries you wrote into the trust.",
            "Beneficiaries receive distributions based on the trust terms. You can set conditions: 'distribute $50K when they turn 25,' 'pay for education expenses only,' or 'monthly income for life.'",
            "When you die, the trust continues operating — no probate, no court involvement, no public record. This is the key advantage over a will, which must go through probate court.",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-indigo-900/30 text-indigo-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-xs text-zinc-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Revocable vs Irrevocable */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <Scales size={14} weight="bold" className="text-amber-400" /> Revocable vs. Irrevocable Trust
          </h3>
          <p className="text-xs text-zinc-500 mt-1">The most important distinction in trust law</p>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-zinc-950/50 border border-blue-900/30 rounded-sm p-4">
              <h4 className="text-xs font-medium text-blue-400 mb-3 flex items-center gap-1.5">
                <Lock size={12} weight="bold" /> Revocable (Living) Trust
              </h4>
              <ul className="space-y-2">
                {[
                  "You maintain full control — can change terms, add/remove assets, or dissolve it entirely.",
                  "You are typically the trustee AND the beneficiary during your lifetime.",
                  "Does NOT protect assets from lawsuits or creditors (because you still control them).",
                  "Does NOT reduce estate taxes (assets are still considered yours for tax purposes).",
                  "Primary benefit: avoids probate. When you die, assets transfer instantly to beneficiaries without court.",
                  "Secondary benefit: privacy. Unlike a will, trusts are not public record.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <ArrowRight size={10} weight="bold" className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-[10px] text-zinc-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-zinc-950/50 border border-violet-900/30 rounded-sm p-4">
              <h4 className="text-xs font-medium text-violet-400 mb-3 flex items-center gap-1.5">
                <ShieldCheck size={12} weight="bold" /> Irrevocable Trust
              </h4>
              <ul className="space-y-2">
                {[
                  "Once created, you CANNOT change it, take assets back, or dissolve it (with rare exceptions).",
                  "Assets are no longer legally yours — they belong to the trust. You give up control.",
                  "DOES protect assets from lawsuits, creditors, and judgments (because you don't own them anymore).",
                  "DOES reduce estate taxes — assets are removed from your taxable estate.",
                  "Can protect assets from nursing home/Medicaid spend-down (if funded 5+ years before needing care).",
                  "Used by the wealthy for generational wealth transfer, asset protection, and tax planning.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <ArrowRight size={10} weight="bold" className="text-violet-400 flex-shrink-0 mt-0.5" />
                    <span className="text-[10px] text-zinc-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Trust vs Will comparison */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <Gavel size={14} weight="bold" className="text-amber-400" /> Trust vs. Will — Why Trusts Win
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" data-testid="trust-vs-will-table">
            <thead>
              <tr className="border-b border-zinc-800/60 bg-zinc-900/60">
                <th className="text-left px-4 py-3 text-zinc-500 font-medium uppercase tracking-wider w-[180px]">Feature</th>
                <th className="text-center px-4 py-3 text-indigo-400 font-medium uppercase tracking-wider">Trust</th>
                <th className="text-center px-4 py-3 text-zinc-500 font-medium uppercase tracking-wider">Will</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Avoids Probate", trust: "Yes — immediate transfer", will: "No — must go through probate court (6-18 months)" },
                { feature: "Privacy", trust: "Private — not public record", will: "Public — anyone can see it after probate" },
                { feature: "Asset Protection", trust: "Yes (irrevocable)", will: "None" },
                { feature: "Tax Benefits", trust: "Yes (irrevocable)", will: "None" },
                { feature: "Control After Death", trust: "Full control — set conditions for decades", will: "Limited — one-time distribution" },
                { feature: "Incapacity Protection", trust: "Yes — trustee manages if you're incapacitated", will: "No — only activates at death" },
                { feature: "Cost to Create", trust: "$1,500 – $5,000+", will: "$300 – $1,000" },
                { feature: "Contestability", trust: "Very hard to contest", will: "Easier to contest in court" },
                { feature: "Speed of Distribution", trust: "Immediate (days)", will: "Months to years (probate)" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-zinc-800/30 hover:bg-zinc-900/30">
                  <td className="px-4 py-2.5 text-zinc-400 font-medium">{row.feature}</td>
                  <td className="px-4 py-2.5 text-zinc-300 text-center">{row.trust}</td>
                  <td className="px-4 py-2.5 text-zinc-300 text-center">{row.will}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   HOW A TRUST PROTECTS ASSETS TAB
   ============================================================ */
function AssetProtectionTab() {
  const scenarios = [
    {
      icon: Gavel, color: "text-red-400", bg: "bg-red-900/20",
      badge: "LAWSUIT", badgeColor: "bg-red-900/40 text-red-400",
      title: "Lawsuit / Legal Judgment",
      without: "You own a rental property in your personal name. A tenant slips, sues you for $500K, and wins. The court can seize your rental property, your bank accounts, your car, and garnish your wages until the judgment is paid.",
      with_trust: "The rental property is owned by an irrevocable trust (or LLC owned by a trust). When the tenant sues, they can only go after the trust's assets tied to that property — not your personal bank accounts, home, or other investments. Your personal wealth is shielded.",
    },
    {
      icon: Heart, color: "text-rose-400", bg: "bg-rose-900/20",
      badge: "DIVORCE", badgeColor: "bg-rose-900/40 text-rose-400",
      title: "Divorce Protection",
      without: "You build a $2M investment portfolio during your marriage. In a divorce, your spouse is entitled to 50% (in community property states) or an 'equitable' share. You lose $1M+ of your wealth.",
      with_trust: "Assets placed in an irrevocable trust before the marriage (or received as inheritance in a trust) are generally NOT considered marital property. A properly structured trust can protect family wealth from being divided in a divorce — for you AND for your children's future marriages.",
    },
    {
      icon: Bank, color: "text-amber-400", bg: "bg-amber-900/20",
      badge: "CREDITORS", badgeColor: "bg-amber-900/40 text-amber-400",
      title: "Creditor Protection",
      without: "You owe $200K in business debts or medical bills. Creditors can place liens on your home, freeze your bank accounts, and force the sale of your assets to recover what you owe.",
      with_trust: "Assets in an irrevocable trust are not yours — they belong to the trust. Creditors cannot touch them because you don't legally own them. This is why doctors, business owners, and real estate investors use irrevocable trusts: one bad deal or one malpractice suit can't wipe out a lifetime of wealth building.",
    },
    {
      icon: CurrencyDollar, color: "text-emerald-400", bg: "bg-emerald-900/20",
      badge: "ESTATE TAX", badgeColor: "bg-emerald-900/40 text-emerald-400",
      title: "Estate Tax Avoidance",
      without: "You die with a $20M estate. The federal estate tax exemption is ~$13M (2024). Everything above $13M is taxed at 40%. Your family pays $2.8M in estate taxes. That's money going to the government instead of your children.",
      with_trust: "You transfer $7M into an irrevocable trust years before death. Your taxable estate is now $13M — under the exemption. Your family pays $0 in estate taxes. The $7M in the trust passes to your beneficiaries tax-free. This is the #1 reason ultra-wealthy families use trusts.",
    },
    {
      icon: Users, color: "text-blue-400", bg: "bg-blue-900/20",
      badge: "MEDICAID", badgeColor: "bg-blue-900/40 text-blue-400",
      title: "Nursing Home / Medicaid Protection",
      without: "You need nursing home care at $10K/month. Medicaid won't pay until you've spent nearly all your assets (the 'spend-down'). Your $400K in savings is drained to $2K before Medicaid kicks in. Your children inherit nothing.",
      with_trust: "Assets transferred to an irrevocable Medicaid Asset Protection Trust at least 5 years before needing care are NOT counted toward Medicaid eligibility. Your $400K is protected in the trust, Medicaid covers your care, and your children inherit the full $400K.",
    },
    {
      icon: Lock, color: "text-violet-400", bg: "bg-violet-900/20",
      badge: "SPENDTHRIFT", badgeColor: "bg-violet-900/40 text-violet-400",
      title: "Protection From Irresponsible Heirs",
      without: "You leave $1M to your 21-year-old child in a will. They blow through it in 3 years on cars, partying, and bad investments. Your life's work is gone.",
      with_trust: "You put the $1M in a spendthrift trust with conditions: $2,000/month allowance until age 30, then $5,000/month. Education and medical expenses paid in full. Principal distributed at ages 30, 35, and 40 in thirds. They can't blow it all at once, and creditors can't touch it either. You control the money from beyond the grave.",
    },
    {
      icon: House, color: "text-teal-400", bg: "bg-teal-900/20",
      badge: "REAL ESTATE", badgeColor: "bg-teal-900/40 text-teal-400",
      title: "Real Estate Asset Protection",
      without: "You own 5 rental properties in your personal name. Someone gets hurt at Property #3 and sues for $1M. All 5 properties, plus your personal assets, are exposed.",
      with_trust: "Each property is held in a separate LLC, and the LLCs are owned by an irrevocable trust. A lawsuit against Property #3 can only reach the assets inside that specific LLC. Your other 4 properties, personal savings, and home are completely isolated. This is the standard structure used by serious real estate investors.",
    },
  ];

  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="asset-protection-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-emerald-900/20 flex items-center justify-center">
            <Lock size={20} weight="bold" className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">How a Trust Protects Your Assets</h2>
            <p className="text-xs text-zinc-400">Real scenarios showing what happens with and without trust protection</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          The core principle is simple: <strong className="text-zinc-100">you can't lose what you don't own.</strong> When assets are transferred to an irrevocable trust, you no longer legally own them. Lawsuits, creditors, divorcing spouses, the IRS, and Medicaid can only come after assets that are legally yours. A trust creates a legal wall between your wealth and anyone trying to take it.
        </p>
      </div>

      {/* Scenarios */}
      <div className="space-y-4">
        {scenarios.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid={`protection-scenario-${i}`}>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-9 h-9 rounded-sm ${s.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} weight="bold" className={s.color} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[8px] px-1.5 py-0.5 border-0 ${s.badgeColor} h-4`}>{s.badge}</Badge>
                      <h3 className="text-sm font-heading font-bold text-zinc-100">{s.title}</h3>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-red-950/20 border border-red-900/20 rounded-sm p-4">
                    <p className="text-[9px] uppercase tracking-wider text-red-400 mb-2 font-medium flex items-center gap-1">
                      <WarningCircle size={10} weight="bold" /> Without a Trust
                    </p>
                    <p className="text-[10px] text-zinc-400 leading-relaxed">{s.without}</p>
                  </div>
                  <div className="bg-emerald-950/20 border border-emerald-900/20 rounded-sm p-4">
                    <p className="text-[9px] uppercase tracking-wider text-emerald-400 mb-2 font-medium flex items-center gap-1">
                      <ShieldCheck size={10} weight="bold" /> With a Trust
                    </p>
                    <p className="text-[10px] text-zinc-400 leading-relaxed">{s.with_trust}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom line */}
      <div className="bg-indigo-900/10 border border-indigo-800/30 rounded-sm p-5" data-testid="protection-bottom-line">
        <h3 className="text-xs font-medium text-indigo-300 mb-3 flex items-center gap-2">
          <WarningDiamond size={14} weight="bold" /> Critical Rules of Asset Protection
        </h3>
        <div className="space-y-2.5">
          {[
            "Timing matters — You must set up protection BEFORE you need it. Transferring assets to a trust while being sued is fraudulent conveyance and a court will reverse it.",
            "The 5-year lookback — For Medicaid, assets must be in the trust for 5+ years before you apply. Plan early.",
            "Irrevocable means irrevocable — Once you give up assets, they're gone. Make sure you can afford to live without them.",
            "State laws vary — Trust laws differ significantly by state. Nevada, South Dakota, and Delaware have the strongest trust protection laws in the U.S.",
            "Work with an attorney — DIY trusts from the internet are usually worthless in court. A qualified estate planning attorney costs $2,000-$5,000 and can protect millions.",
          ].map((rule, i) => (
            <div key={i} className="flex items-start gap-2">
              <ArrowRight size={10} weight="bold" className="text-indigo-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-zinc-300 leading-relaxed">{rule}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   TYPES OF TRUSTS TAB
   ============================================================ */
function TypesOfTrustsTab() {
  const trusts = [
    {
      name: "Revocable Living Trust",
      color: "text-blue-400", border: "border-blue-900/30",
      purpose: "Avoid probate and maintain control during your lifetime.",
      best_for: "Everyone with assets — the foundation of any estate plan.",
      key_point: "You control everything while alive. At death, assets pass instantly to beneficiaries without probate. Does NOT protect from lawsuits or reduce taxes.",
    },
    {
      name: "Irrevocable Life Insurance Trust (ILIT)",
      color: "text-violet-400", border: "border-violet-900/30",
      purpose: "Remove life insurance proceeds from your taxable estate.",
      best_for: "Anyone with a life insurance policy over $1M.",
      key_point: "The trust owns your life insurance policy. When you die, the death benefit goes into the trust (not to you), so it's not part of your estate. A $3M policy in an ILIT saves your family ~$1.2M in estate taxes (at the 40% rate).",
    },
    {
      name: "Asset Protection Trust (APT)",
      color: "text-emerald-400", border: "border-emerald-900/30",
      purpose: "Shield assets from future creditors, lawsuits, and judgments.",
      best_for: "Doctors, business owners, real estate investors — anyone in a high-liability profession.",
      key_point: "Domestic APTs (Nevada, South Dakota, Delaware) offer strong protection. Offshore APTs (Cook Islands, Nevis) offer even stronger protection — nearly impossible for U.S. courts to pierce.",
    },
    {
      name: "Charitable Remainder Trust (CRT)",
      color: "text-amber-400", border: "border-amber-900/30",
      purpose: "Sell appreciated assets without paying capital gains tax, receive income, and donate the remainder to charity.",
      best_for: "People holding highly appreciated stock, real estate, or crypto with large unrealized gains.",
      key_point: "You transfer $1M in stock (cost basis $100K) into a CRT. The trust sells it — $0 capital gains tax. You receive 5-8% annual income for life. When you die, the remainder goes to charity and your estate gets a tax deduction.",
    },
    {
      name: "Spendthrift Trust",
      color: "text-rose-400", border: "border-rose-900/30",
      purpose: "Protect beneficiaries from themselves and from creditors.",
      best_for: "Parents leaving money to children or heirs who may not handle a lump sum responsibly.",
      key_point: "Beneficiaries receive controlled distributions (monthly income, education expenses, etc.) but can't access or pledge the principal. Creditors of the beneficiary also cannot touch the trust assets. You set the rules, and the trustee enforces them.",
    },
    {
      name: "Dynasty Trust",
      color: "text-orange-400", border: "border-orange-900/30",
      purpose: "Pass wealth down for multiple generations without estate tax at each generation.",
      best_for: "Ultra-wealthy families building generational wealth.",
      key_point: "Normally, wealth is taxed at 40% each time it passes to the next generation. A dynasty trust skips this — assets grow and pass down for 100+ years (forever in some states) without estate tax. The Rockefellers, Waltons, and other billionaire families all use dynasty trusts.",
    },
    {
      name: "Qualified Personal Residence Trust (QPRT)",
      color: "text-teal-400", border: "border-teal-900/30",
      purpose: "Transfer your home to heirs at a massive discount for gift tax purposes.",
      best_for: "People with expensive homes who want to pass them to children.",
      key_point: "You transfer your $1M home into a QPRT and retain the right to live in it for 15 years. For gift tax purposes, the value is discounted to ~$400K (because you kept the right to live there). After 15 years, the home passes to your children — and any appreciation is also outside your estate.",
    },
    {
      name: "Special Needs Trust (SNT)",
      color: "text-sky-400", border: "border-sky-900/30",
      purpose: "Provide for a disabled family member without disqualifying them from government benefits.",
      best_for: "Families with a member receiving SSI, Medicaid, or other means-tested benefits.",
      key_point: "A direct inheritance would disqualify a disabled person from benefits. An SNT holds the funds and pays for supplemental needs (therapy, electronics, travel) without being counted as the beneficiary's assets. Benefits continue uninterrupted.",
    },
  ];

  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="types-of-trusts-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-amber-900/20 flex items-center justify-center">
            <TreeStructure size={20} weight="bold" className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Types of Trusts</h2>
            <p className="text-xs text-zinc-400">Each type serves a specific purpose — choose based on what you need to protect</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          There isn't one "best" trust — there are trusts designed for specific situations. A revocable living trust avoids probate. An irrevocable trust protects assets. A dynasty trust builds generational wealth. The wealthy don't pick one — they use several in combination. Here are the most important types and when to use each.
        </p>
      </div>

      {/* Trust cards */}
      <div className="space-y-4">
        {trusts.map((t, i) => (
          <div key={i} className={`bg-zinc-900/40 border ${t.border} rounded-sm p-5`} data-testid={`trust-type-${i}`}>
            <h3 className={`text-sm font-heading font-bold ${t.color} mb-2`}>{t.name}</h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-[9px] uppercase tracking-wider text-zinc-600 w-[70px] flex-shrink-0 pt-0.5">Purpose</span>
                <p className="text-xs text-zinc-300 leading-relaxed">{t.purpose}</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[9px] uppercase tracking-wider text-zinc-600 w-[70px] flex-shrink-0 pt-0.5">Best For</span>
                <p className="text-xs text-zinc-300 leading-relaxed">{t.best_for}</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[9px] uppercase tracking-wider text-zinc-600 w-[70px] flex-shrink-0 pt-0.5">Key Point</span>
                <p className="text-xs text-zinc-400 leading-relaxed">{t.key_point}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action steps */}
      <div className="bg-emerald-900/10 border border-emerald-800/30 rounded-sm p-5" data-testid="trust-action-steps">
        <h3 className="text-xs font-medium text-emerald-300 mb-3 flex items-center gap-2">
          <HandCoins size={14} weight="bold" /> Getting Started — Practical Steps
        </h3>
        <div className="space-y-3">
          {[
            { step: "Step 1", desc: "Start with a Revocable Living Trust — this is the foundation. Put your home, bank accounts, and brokerage accounts in it. Cost: $1,500-$3,000 with an attorney." },
            { step: "Step 2", desc: "If you have life insurance over $500K, create an ILIT to own the policy. This removes the death benefit from your taxable estate." },
            { step: "Step 3", desc: "If you own a business or rental properties, consider an Asset Protection Trust or holding LLCs inside a trust for liability isolation." },
            { step: "Step 4", desc: "If your net worth exceeds $5M, talk to an estate planning attorney about dynasty trusts and advanced strategies for generational wealth transfer." },
            { step: "Step 5", desc: "Review and update your trust every 3-5 years or after major life events (marriage, divorce, birth of children, large asset purchases)." },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-900/30 text-emerald-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <div>
                <p className="text-xs font-medium text-emerald-400">{s.step}</p>
                <p className="text-[10px] text-zinc-400 leading-relaxed mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE — Tabbed Layout
   ============================================================ */
const SUB_TABS = [
  { key: "what-is", label: "What is a Trust", icon: ShieldCheck },
  { key: "protection", label: "Asset Protection", icon: Lock },
  { key: "types", label: "Types of Trusts", icon: TreeStructure },
];

export default function Trust() {
  const [tab, setTab] = useState("what-is");

  return (
    <div data-testid="trust-page">
      <div className="bg-zinc-950 border-b border-zinc-800/60 px-5 pt-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck size={22} weight="bold" className="text-indigo-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Trust</h1>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-indigo-900/30 text-indigo-400 h-4">EDUCATION</Badge>
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-9 bg-zinc-900/60 border border-zinc-800/60 p-0.5 gap-0.5" data-testid="trust-tabs">
            {SUB_TABS.map(t => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`trust-tab-${t.key}`}
                  className="text-[11px] px-3 py-1.5 h-8 gap-1.5 font-medium transition-colors data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 data-[state=active]:shadow-sm">
                  <Icon size={13} weight="bold" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      <div data-testid={`trust-content-${tab}`}>
        {tab === "what-is" && <WhatIsATrustTab />}
        {tab === "protection" && <AssetProtectionTab />}
        {tab === "types" && <TypesOfTrustsTab />}
      </div>
    </div>
  );
}
